package com.eladfinish.bible.contest.screens.main.ui

import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.ic_credit_card
import com.eladfinish.bible.contest.purchase_remove_ads
import com.eladfinish.bible.contest.screens.main.MainScreenAction
import com.eladfinish.bible.contest.screens.main.MainScreenState
import com.eladfinish.bible.contest.screens.main.MainScreenStore
import com.eladfinish.bible.contest.screens.main.RemoveAdsSource
import com.eladfinish.bible.contest.screens.main.data.ButtonData
import com.eladfinish.bible.contest.screens.main.data.ButtonIcon
import com.eladfinish.bible.contest.screens.main.data.ButtonType
import com.eladfinish.bible.contest.screens.main.data.ItemMode
import com.eladfinish.bible.contest.screens.main.data.RandomVerseIcon
import com.eladfinish.bible.contest.screens.main.data.toButtonDataList
import com.eladfinish.bible.contest.screens.main.ui.dialogs.MainScreenDialogs
import com.eladfinish.bible.contest.screens.main.usecases.GetMainScreenButtonListUseCase
import com.eladfinish.bible.contest.title_random_verse
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import com.eladfinish.bible.contest.ui.extensions.disableSplitMotionEvents
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.layout.WithAdaptiveGridColumns
import com.eladfinish.bible.contest.ui.layout.readableWidth
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList
import org.koin.compose.viewmodel.koinViewModel

@Composable
internal fun MainScreen(
    mainScreenStore: MainScreenStore,
    onClick: (ButtonType) -> Unit,
    onLongClick: (ButtonType) -> Unit,
    onHebrewGenderConfirmed: (HebrewGender) -> Unit,
    showToast: (StringDesc) -> Unit,
    logEvent: (String) -> Unit,
) {
    val mainState: MainScreenState by mainScreenStore.observeState().collectAsState()

    when (mainState.itemMode) {
        ItemMode.REGULAR -> {
            MainScreenRegular(
                mainScreenStore = mainScreenStore,
                headerAnnouncement = mainState.announcement,
                onAnnouncementClicked = {
                    mainScreenStore.dispatch(MainScreenAction.AnnouncementClicked)
                },
                buttonDataList = mainState.buttonDataList,
                onClick = onClick,
                onLongClick = onLongClick,
                isDebug = mainState.isDebugMode,
                hebrewGender = mainState.hebrewGender,
                hebrewGenderDialogAlreadyShown = mainState.hebrewGenderDialogAlreadyShown,
                onHebrewGenderConfirmed = {
                    mainScreenStore.dispatch(MainScreenAction.UpdateHebrewGender(hebrewGender = it))
                    onHebrewGenderConfirmed(it)
                },
                canPurchaseAdFree = mainState.canPurchaseAdFree,
                onRemoveAdsClicked = {
                    mainScreenStore.dispatch(MainScreenAction.RemoveAdsClicked(source = RemoveAdsSource.MENU_BUTTON))
                },
                onRemoveAdsButtonShown = {
                    mainScreenStore.dispatch(MainScreenAction.RemoveAdsButtonShown)
                },
            )
        }

        ItemMode.DRAGGABLE ->
            MainScreenDraggable(
                buttonDataList = mainState.buttonDataList,
                onDoneDragging = {
                    mainScreenStore.dispatch(MainScreenAction.DoneDragMode(newOrder = it))
                },
                onResetOrder = {
                    mainScreenStore.dispatch(MainScreenAction.ResetOrder)
                },
                showToast = showToast,
                hebrewGender = mainState.hebrewGender,
                onBackPressed = {
                    mainScreenStore.dispatch(MainScreenAction.CancelDragMode)
                }
            )
    }

    MainScreenDialogs(
        logEvent = logEvent,
        dialogState = mainState.dialogState,
        onAction = mainScreenStore::dispatch,
    )
}

@Composable
private fun MainScreenRegular(
    mainScreenStore: MainScreenStore = koinViewModel(),
    headerAnnouncement: String,
    onAnnouncementClicked: () -> Unit,
    buttonDataList: ImmutableList<Pair<ButtonType, ButtonData>>,
    onClick: (ButtonType) -> Unit,
    onLongClick: (ButtonType) -> Unit,
    isDebug: Boolean = false,
    hebrewGender: HebrewGender,
    hebrewGenderDialogAlreadyShown: Boolean,
    onHebrewGenderConfirmed: (HebrewGender) -> Unit,
    canPurchaseAdFree: Boolean,
    onRemoveAdsClicked: () -> Unit,
    onRemoveAdsButtonShown: () -> Unit,
) {
    if (!hebrewGenderDialogAlreadyShown) {
        HebrewGenderDialog(
            onHebrewGenderConfirmed = onHebrewGenderConfirmed,
        )
    }

    MainScreenContent(
        mainScreenStore = mainScreenStore,
        buttonDataList = buttonDataList,
        headerAnnouncement = headerAnnouncement,
        onAnnouncementClicked = onAnnouncementClicked,
        onClick = onClick,
        onLongClick = onLongClick,
        isDebug = isDebug,
        hebrewGender = hebrewGender,
        canPurchaseAdFree = canPurchaseAdFree,
        onRemoveAdsClicked = onRemoveAdsClicked,
        onRemoveAdsButtonShown = onRemoveAdsButtonShown,
    )
}

@Composable
private fun MainScreenContent(
    mainScreenStore: MainScreenStore,
    buttonDataList: ImmutableList<Pair<ButtonType, ButtonData>>,
    headerAnnouncement: String,
    onAnnouncementClicked: () -> Unit,
    onClick: (ButtonType) -> Unit,
    onLongClick: (ButtonType) -> Unit,
    isDebug: Boolean,
    hebrewGender: HebrewGender,
    canPurchaseAdFree: Boolean,
    onRemoveAdsClicked: () -> Unit,
    onRemoveAdsButtonShown: () -> Unit,
) {
    var showHeaderAnnouncement by rememberSaveable { mutableStateOf(true) }

    Column {
        if (showHeaderAnnouncement) {
            Text(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(all = 1.dp)
                    .combinedClickable(
                        onClick = onAnnouncementClicked,
                        onLongClick = { showHeaderAnnouncement = false }
                    ),
                text = headerAnnouncement,
                color = Color(color = 0xFF808080),
                textAlign = TextAlign.Center,
                style = MaterialTheme.typography.bodyLarge.copy(
                    fontSize = 21.sp,
                ),
            )
        }
        GridWithButtons(
            buttonDataList = buttonDataList,
            canPurchaseAdFree = canPurchaseAdFree,
            onRemoveAdsClicked = onRemoveAdsClicked,
            onRemoveAdsButtonShown = onRemoveAdsButtonShown,
            onClick = {
                mainScreenStore.dispatch(
                    MainScreenAction.ButtonClicked(
                        buttonType = it,
                        position = buttonDataList.indexOfFirst { (buttonType, _) -> buttonType == it },
                    )
                )
                var propagateClick = true
                when (it) {
                    ButtonType.REORDER -> {
                        mainScreenStore.dispatch(MainScreenAction.SwitchToDragMode)
                        propagateClick = false
                    }

                    ButtonType.HELP -> {
                        mainScreenStore.dispatch(MainScreenAction.HelpClicked)
                    }

                    ButtonType.ABOUT -> {
                        mainScreenStore.dispatch(MainScreenAction.AboutClicked)
                    }

                    else -> Unit
                }
                if (propagateClick) {
                    onClick(it)
                }
            },
            onLongClick = onLongClick,
            isDebug = isDebug,
            hebrewGender = hebrewGender,
        )
    }
}

@Composable
private fun GridWithButtons(
    buttonDataList: ImmutableList<Pair<ButtonType, ButtonData>>,
    onClick: (ButtonType) -> Unit,
    onLongClick: (ButtonType) -> Unit,
    isDebug: Boolean = false,
    hebrewGender: HebrewGender,
    canPurchaseAdFree: Boolean = false,
    onRemoveAdsClicked: () -> Unit = {},
    onRemoveAdsButtonShown: () -> Unit = {},
) {
    WithAdaptiveGridColumns(
        narrowColumns = 2,
        wideColumns = 4,
    ) { columnsCount ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .disableSplitMotionEvents()
                .verticalScrollWithScrollbar()
                // Inside the scroll: the grid draws behind the navigation bar and its last row
                // still scrolls up above it.
                .navigationBarInsetPadding()
                .padding(8.dp),
            // Centered (not just spacedBy) so the grid sits in the middle of the window on a
            // large desktop screen instead of clumping at the top with empty space below — a
            // no-op on phone/tablet, where the content already fills most of the screen and
            // there's no leftover space to distribute. Still scrolls normally if content is
            // taller than the viewport.
            verticalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterVertically),
        ) {
            // Rows of buttons — each row uses IntrinsicSize.Max so both buttons
            // in a row share the height of the taller one.
            buttonDataList.chunked(columnsCount).forEach { rowItems ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(IntrinsicSize.Max),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    rowItems.forEach { (buttonType, buttonData) ->
                        IconTextButton(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxHeight(),
                            stringResource = buttonData.stringResource,
                            icon = buttonData.icon,
                            onClick = { onClick(buttonType) },
                            onLongClick = { onLongClick(buttonType) },
                            itemMode = ItemMode.REGULAR,
                            isDebug = isDebug,
                            hebrewGender = hebrewGender,
                        )
                    }
                    // Fill leftover space in the last (possibly incomplete) row
                    repeat(columnsCount - rowItems.size) {
                        Spacer(Modifier.weight(1f))
                    }
                }
            }

            // Full-width "remove ads" button at the bottom of the list.
            // In Column, no span API is needed — fillMaxWidth() covers the full row.
            if (canPurchaseAdFree) {
                // Counted when the button first has an area inside the scrolled viewport, once per
                // composition of it: it is the last item of the scrolling list, so composing it
                // does not mean the user saw it.
                var shownReported by rememberSaveable { mutableStateOf(false) }
                IconTextButton(
                    modifier = Modifier
                        .readableWidth()
                        .onGloballyPositioned { coordinates ->
                            val visible = coordinates.boundsInWindow().let { it.width > 0f && it.height > 0f }
                            if (visible && !shownReported) {
                                shownReported = true
                                onRemoveAdsButtonShown()
                            }
                        },
                    stringResource = SharedRes.strings.purchase_remove_ads,
                    icon = ButtonIcon.Resource(SharedRes.images.ic_credit_card),
                    onClick = onRemoveAdsClicked,
                    onLongClick = {},
                    hebrewGender = hebrewGender,
                )
            }
        }
    }
}

@Preview
@Composable
private fun GridWithButtonsPreview() {
    GridWithButtons(
        buttonDataList = GetMainScreenButtonListUseCase().invoke(isDebugMode = true).toButtonDataList(),
        onClick = {},
        onLongClick = {},
        hebrewGender = HebrewGender.MALE,
    )
}

@Preview
@Composable
private fun GridWithButtonsPreviewEnglish() {
    GridWithButtons(
        buttonDataList = GetMainScreenButtonListUseCase().invoke(isDebugMode = true).toButtonDataList(),
        onClick = {},
        onLongClick = {},
        hebrewGender = HebrewGender.MALE,
    )
}

@Preview
@Composable
private fun IconTextButtonPreview() {
    IconTextButton(
        stringResource = SharedRes.strings.title_random_verse,
        icon = ButtonIcon.Vector(RandomVerseIcon),
        onClick = {},
        onLongClick = {},
        hebrewGender = HebrewGender.MALE,
    )
}
